<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SeatOrdersAPI from 'dashboard/api/seatOrders';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  planKey: { type: String, default: 'standard' },
  billingCycle: { type: String, default: 'monthly' },
});

const { t } = useI18n();
const { currentAccount } = useAccount();

const isVisible = ref(false);
const quantity = ref(1);
const isLoading = ref(false);
const error = ref(null);
const showPayment = ref(false);
const paymentUrl = ref('');
const planConfig = ref(null);

// 升级抵扣相关
const isUpgrade = ref(false);
const creditAmount = ref(0);
const remainingDays = ref(0);

const customAttributes = computed(
  () => currentAccount.value?.custom_attributes || {}
);
const currentPlanName = computed(() => {
  const name = customAttributes.value.plan_name || '';
  const map = { trial: '试用版', standard: '标准版', professional: '专业版', enterprise: '企业版' };
  return map[name] || name || '未订阅';
});
const currentExpiryDate = computed(() => {
  const raw = customAttributes.value.subscription_ends_on;
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
});

const planTitleMap = { standard: '标准版', professional: '专业版' };
const cycleLabelMap = { monthly: '月付', yearly: '年付' };

const activePlanDetails = computed(() => {
  if (!planConfig.value) return null;
  const key = props.planKey === 'professional' ? 'pro' : 'standard';
  const cycle = props.billingCycle === 'yearly' ? 'yearly' : 'monthly';
  return planConfig.value[`${key}_${cycle}`];
});

const displayPlanName = computed(
  () => planTitleMap[props.planKey] || props.planKey
);
const displayCycleLabel = computed(
  () => cycleLabelMap[props.billingCycle] || props.billingCycle
);

const calculatedExpiry = computed(() => {
  if (!activePlanDetails.value) return '';
  const monthsPerUnit = props.billingCycle === 'yearly' ? 12 : 1;
  const totalMonths = monthsPerUnit * quantity.value;

  let base;
  if (isUpgrade.value) {
    // 升级：从今天开始计算
    base = new Date();
  } else {
    // 续费：从旧到期日开始接续
    const currentExpiry = currentExpiryDate.value
      ? new Date(currentExpiryDate.value)
      : null;
    base = currentExpiry && currentExpiry > new Date() ? currentExpiry : new Date();
  }

  const newDate = new Date(base);
  newDate.setMonth(newDate.getMonth() + totalMonths);
  return `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}`;
});

const originalTotalPrice = computed(() => {
  if (!activePlanDetails.value) return 0;
  return quantity.value * activePlanDetails.value.price;
});

const creditDeduction = computed(() => (isUpgrade.value ? creditAmount.value : 0));

const actualPrice = computed(() =>
  Math.max(0, originalTotalPrice.value - creditDeduction.value)
);

const displayOriginalPrice = computed(() =>
  (originalTotalPrice.value / 100).toFixed(2)
);
const displayCredit = computed(() =>
  (creditDeduction.value / 100).toFixed(2)
);
const displayActualPrice = computed(() =>
  (actualPrice.value / 100).toFixed(2)
);

const formatPrice = cents => (cents / 100).toFixed(2);

const decreaseQuantity = () => {
  if (quantity.value > 1) quantity.value -= 1;
};
const increaseQuantity = () => {
  quantity.value += 1;
};
const handleQuantityInput = event => {
  const value = parseInt(event.target.value, 10);
  if (Number.isNaN(value) || value < 1) {
    quantity.value = 1;
  } else {
    quantity.value = value;
  }
};

const fetchPlanConfig = async () => {
  try {
    const response = await SeatOrdersAPI.getPlanConfig();
    planConfig.value = response.data;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[BuyPlanModal] 获取套餐配置失败:', err);
  }
};

const fetchUpgradePreview = async () => {
  if (!activePlanDetails.value?.product_no) {
    // eslint-disable-next-line no-console
    console.warn('[BuyPlanModal] 套餐配置未加载，无法获取升级预览');
    return;
  }

  // 直接调用后端 API，让后端判断是否是升级以及计算抵扣金额
  // 前端不再进行前置判断，完全依赖后端返回结果
  try {
    const response = await SeatOrdersAPI.getUpgradePreview(
      activePlanDetails.value.product_no,
      quantity.value
    );

    if (response.data.blocked) {
      // 处理差价为负的情况，显示建议的最小数量
      if (response.data.blocked_reason === '升级差价为负数') {
        error.value = t('PLAN_ORDER.PRICE_DIFFERENCE_NEGATIVE', {
          minQuantity: response.data.min_quantity
        });
      } else {
        error.value = response.data.blocked_reason;
      }
      isUpgrade.value = false;
      creditAmount.value = 0;
      remainingDays.value = 0;
      return;
    }

    // 使用后端返回的判断结果
    isUpgrade.value = response.data.is_upgrade || false;
    creditAmount.value = response.data.credit_amount || 0;
    remainingDays.value = response.data.remaining_days || 0;

    // eslint-disable-next-line no-console
    console.log('[BuyPlanModal] 升级预览:', {
      isUpgrade: isUpgrade.value,
      creditAmount: creditAmount.value,
      remainingDays: remainingDays.value,
      productNo: activePlanDetails.value.product_no,
      quantity: quantity.value,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[BuyPlanModal] 获取升级预览失败:', err);
    isUpgrade.value = false;
    creditAmount.value = 0;
    remainingDays.value = 0;
  }
};

// 监听数量变化，重新计算升级预览
watch(quantity, () => {
  if (quantity.value > 0 && isVisible.value) {
    fetchUpgradePreview();
  }
});

const close = () => {
  quantity.value = 1;
  error.value = null;
  showPayment.value = false;
  paymentUrl.value = '';
  isUpgrade.value = false;
  creditAmount.value = 0;
  remainingDays.value = 0;
  isVisible.value = false;
};

const handlePaymentMessage = event => {
  try {
    const expectedOrigin = paymentUrl.value
      ? new URL(paymentUrl.value).origin
      : null;
    if (!expectedOrigin || event.origin !== expectedOrigin) return;
  } catch {
    return;
  }
  if (event.data === 'payment_success' || event.data === 'payment_closed') {
    close();
    window.location.reload();
  }
};

onMounted(() => {
  window.addEventListener('message', handlePaymentMessage);
});
onUnmounted(() => {
  window.removeEventListener('message', handlePaymentMessage);
});

const open = async () => {
  quantity.value = 1;
  error.value = null;
  showPayment.value = false;
  paymentUrl.value = '';
  isUpgrade.value = false;
  creditAmount.value = 0;
  remainingDays.value = 0;
  isVisible.value = true;
  await fetchPlanConfig();
  await fetchUpgradePreview();
};

const handlePurchase = async () => {
  if (quantity.value < 1) {
    error.value = '购买数量必须大于0';
    return;
  }
  if (!activePlanDetails.value) {
    error.value = '套餐配置加载失败，请稍后重试';
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    const response = await SeatOrdersAPI.createOrder({
      quantity: quantity.value,
      parentOrigin: window.location.origin,
      productNo: activePlanDetails.value.product_no,
      orderType: 'plan',
    });
    const { payment_url: url } = response.data;
    if (url) {
      paymentUrl.value = url;
      showPayment.value = true;
    }
  } catch (err) {
    error.value =
      err.response?.data?.error || '创建订单失败，请稍后重试';
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <Teleport to="body">
    <div
      v-if="isVisible"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50"
      @click.self="close"
    >
      <!-- 支付页面 iframe 模式 -->
      <div
        v-if="showPayment"
        class="w-[550px] max-w-[95vw] h-[550px] max-h-[85vh] bg-white rounded-xl shadow-xl flex flex-col overflow-hidden flex-shrink-0"
      >
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0"
        >
          <h3 class="text-lg font-medium text-slate-900">
            {{ t('PLAN_ORDER.PAYMENT_TITLE') }}
          </h3>
          <button
            type="button"
            class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            @click="close"
          >
            <i class="i-lucide-x text-xl" />
          </button>
        </div>
        <div class="flex-1 overflow-hidden relative p-6">
          <iframe
            :src="paymentUrl"
            class="w-full h-full border-0 rounded-lg"
            sandbox="allow-scripts allow-same-origin allow-forms allow-top-navigation"
          />
        </div>
      </div>

      <!-- 购买表单模式 -->
      <div v-else class="w-full max-w-md p-6 bg-white rounded-xl shadow-xl">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-slate-900">
            {{ t('PLAN_ORDER.TITLE') }}
          </h3>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600"
            @click="close"
          >
            <i class="i-lucide-x text-xl" />
          </button>
        </div>

        <div class="flex flex-col gap-4">
          <!-- 当前套餐信息 -->
          <div class="p-3 bg-blue-50 rounded-lg space-y-1">
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500">{{ t('PLAN_ORDER.CURRENT_PLAN') }}</span>
              <span class="font-medium text-slate-900">{{ currentPlanName }}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500">{{ t('PLAN_ORDER.CURRENT_EXPIRY') }}</span>
              <span class="font-medium text-slate-900">{{ currentExpiryDate || '未设置' }}</span>
            </div>
            <div
              v-if="isUpgrade && remainingDays > 0"
              class="flex items-center justify-between text-sm pt-1 border-t border-blue-200"
            >
              <span class="text-orange-600 font-medium">{{ t('PLAN_ORDER.REMAINING_DAYS', { days: remainingDays }) }}</span>
              <span class="text-orange-600 text-xs">{{ t('PLAN_ORDER.UPGRADE_NOTICE') }}</span>
            </div>
          </div>

          <!-- 购买产品信息 -->
          <div class="p-4 bg-gray-50 rounded-lg">
            <div class="flex items-center gap-3 mb-2">
              <div
                class="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-500"
              >
                <i class="i-lucide-crown text-white text-lg" />
              </div>
              <div>
                <div class="font-medium text-slate-900">
                  {{ displayPlanName }} {{ displayCycleLabel }}
                </div>
                <div
                  v-if="activePlanDetails"
                  class="text-sm text-slate-500"
                >
                  {{ activePlanDetails.seat }}个坐席 /
                  {{ (activePlanDetails.token / 10000).toFixed(0) }}万Token
                </div>
              </div>
            </div>
            <div
              class="flex items-center justify-between mt-3 pt-3 border-t border-gray-200"
            >
              <span class="text-sm text-slate-500">{{
                t('PLAN_ORDER.UNIT_PRICE')
              }}</span>
              <span class="font-medium text-slate-900">
                ¥{{ activePlanDetails ? formatPrice(activePlanDetails.price) : '--' }}
                / {{ displayCycleLabel === '年付' ? '年' : '月' }}
              </span>
            </div>
          </div>

          <!-- 数量选择 -->
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-slate-700">
              {{ t('PLAN_ORDER.QUANTITY') }}（{{ displayCycleLabel === '年付' ? '年' : '月' }}数）
            </label>
            <div class="flex items-center gap-3">
              <button
                type="button"
                class="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                :disabled="quantity <= 1"
                @click="decreaseQuantity"
              >
                <i class="i-lucide-minus text-slate-600" />
              </button>
              <input
                type="number"
                :value="quantity"
                min="1"
                class="w-20 h-10 text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                @input="handleQuantityInput"
              />
              <button
                type="button"
                class="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                @click="increaseQuantity"
              >
                <i class="i-lucide-plus text-slate-600" />
              </button>
            </div>
          </div>

          <!-- 购买后到期日期 -->
          <div class="flex items-center justify-between p-3 bg-green-50 rounded-lg text-sm">
            <span class="text-green-700 font-medium">{{ t('PLAN_ORDER.NEW_EXPIRY') }}</span>
            <span class="font-bold text-green-700">{{ calculatedExpiry || '--' }}</span>
          </div>

          <!-- 价格明细 -->
          <div class="p-4 bg-blue-50 rounded-lg space-y-2">
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500">{{ t('PLAN_ORDER.ORIGINAL_PRICE') }}</span>
              <span class="text-slate-700">¥{{ displayOriginalPrice }}</span>
            </div>
            <div
              v-if="isUpgrade && creditDeduction > 0"
              class="flex items-center justify-between text-sm"
            >
              <span class="text-green-600 font-medium">{{ t('PLAN_ORDER.CREDIT_DEDUCTION') }}</span>
              <span class="text-green-600 font-medium">-¥{{ displayCredit }}</span>
            </div>
            <div class="flex items-center justify-between pt-2 border-t border-blue-200">
              <span class="text-sm font-medium text-blue-700">
                {{ isUpgrade ? t('PLAN_ORDER.ACTUAL_PRICE') : t('PLAN_ORDER.TOTAL') }}
              </span>
              <span class="text-xl font-bold text-blue-700">
                ¥{{ isUpgrade ? displayActualPrice : displayOriginalPrice }}
              </span>
            </div>
          </div>

          <!-- 错误信息 -->
          <div v-if="error" class="text-sm text-red-500">
            {{ error }}
          </div>

          <!-- 按钮 -->
          <div class="flex gap-3 mt-2">
            <button
              type="button"
              class="flex-1 px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              :disabled="isLoading"
              @click="close"
            >
              {{ t('PLAN_ORDER.CANCEL') }}
            </button>
            <button
              type="button"
              class="flex-1 px-4 py-2 bg-woot-500 text-white font-medium rounded-lg hover:bg-woot-600 transition-colors disabled:opacity-50"
              :disabled="isLoading"
              @click="handlePurchase"
            >
              {{
                isLoading ? t('PLAN_ORDER.LOADING') : t('PLAN_ORDER.PURCHASE')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
